import React, { useMemo } from "react";

import {
  type ChartDataset,
  type ChartOptions,
  type TooltipItem,
} from "chart.js";
import chroma from "chroma-js";
import { Bar } from "react-chartjs-2";

import { customTooltip } from "@components/reports/charts";
import { useUser } from "@components/user-context";
import { type GetOverviewPerformance_me_overviewPage_performanceMetrics as OverviewPageMetric } from "@north-beam/nb-client-side";
import { dateRangeLength, formatting } from "@north-beam/nb-common";
import { spaceColorScheme } from "@pages/objects/label-colors";
import { fmtDateInterval } from "@utils/index";

export const MetricBarChart = ({
  selectedMetric,
}: {
  selectedMetric: OverviewPageMetric;
}) => {
  const { user } = useUser();
  const dateLabels = useMemo(
    () => selectedMetric.timeSeriesPointDateRanges.map(fmtDateInterval),
    [selectedMetric],
  );

  const numDatasets = selectedMetric.timeSeriesPointLabels.length;

  const datasets: ChartDataset<"bar">[] = useMemo(() => {
    const iterateNumber = Math.min(
      numDatasets,
      selectedMetric.timeSeriesPointData.length,
    );
    const colors = spaceColorScheme(iterateNumber);
    const rv = [];
    const timeRangeBucketIsFull = selectedMetric.timeSeriesPointDateRanges.map(
      (v) => dateRangeLength(v) >= selectedMetric.windowSizeDays,
    );

    for (let i = 0; i < iterateNumber; ++i) {
      const label = selectedMetric.timeSeriesPointLabels[i];
      const data = selectedMetric.timeSeriesPointData[i].map(Number);
      const color = colors[i];
      const bgColor = chroma(color).alpha(1.0).hex("rgba");

      const dataset: ChartDataset<"bar"> = {
        label,
        data,
        borderWidth: 1,
        barPercentage: 0.75,
        categoryPercentage: 0.7,
        line: 0,
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        backgroundColor: timeRangeBucketIsFull.map((v) =>
          v ? bgColor : createDiagonalPattern(bgColor),
        ),
        borderColor: color,
        elements: {
          point: {
            borderColor: color,
            backgroundColor: color,
            borderWidth: 0,
            hoverRadius: 3.5,
            hoverBorderWidth: 0,
            radius: 0,
            hitRadius: 0,
          },
        },
      };

      rv.push(dataset);
    }
    return rv;
  }, [numDatasets, selectedMetric]);

  const options: ChartOptions<"bar"> = useMemo(() => {
    let label: (item: TooltipItem<"bar">) => string[];

    if (selectedMetric.timeSeriesDisplayMode === "stack") {
      label = ({ raw, datasetIndex, dataIndex }) => {
        return [
          formatting.formatMetric.exact(Number(raw), selectedMetric.format, {
            code: user.currencies.currentCurrency,
          }),
          datasetIndex === 0
            ? "-"
            : `(${formatting.formatMetric.exact(
                Math.round(
                  (Number(raw) - datasets[datasetIndex - 1].data?.[dataIndex] ||
                    0) * 100,
                ) / 100,
                selectedMetric.format,
                {
                  code: user.currencies.currentCurrency,
                },
              )})`,
        ];
      };
    } else {
      label = ({ raw }) => [
        formatting.formatMetric.exact(Number(raw), selectedMetric.format, {
          code: user.currencies.currentCurrency,
        }),
      ];
    }
    return {
      responsive: true,
      maintainAspectRatio: false,
      resizeDelay: 400,
      transitions: {
        resize: {
          animation: {
            duration: 400,
          },
        },
      },
      plugins: {
        legend: {
          display: false,
        },
        tooltip: {
          enabled: false,
          mode: "index",
          intersect: false,
          external: customTooltip,
          itemSort: (a: TooltipItem<"bar">, b: TooltipItem<"bar">) =>
            b.datasetIndex - a.datasetIndex,
          callbacks: {
            label,
            beforeLabel: (v: TooltipItem<"bar">) =>
              datasets[v.datasetIndex ?? 0].label ?? "",
          },
        },
      },
      scales: {
        y: {
          position: "left",
          grid: {
            tickLength: 0,
          },
          beginAtZero: true,
          ticks: {
            maxTicksLimit: 4,
            padding: 10,
            font: {
              family: "Commons Pro Mono",
              size: 11,
            },
            callback: (value: string | number) =>
              formatting.formatMetric.approximate(
                Number(value),
                selectedMetric.format,
                {
                  code: user.currencies.currentCurrency,
                },
              ),
          },
          afterFit: (scaleInstance) => {
            scaleInstance.width = 60;
          },
          stacked: false,
        },
        x: {
          grid: {
            drawOnChartArea: false,
            drawTicks: false,
            drawBorder: false,
            display: false,
          },
          ticks: {
            padding: 5,
            autoSkip: true,
            maxRotation: 0,
            maxTicksLimit: 5,
            callback: (_val, index: number) => {
              const value = dateLabels[index];
              return value?.split(" — ")?.[1] ?? value;
            },
            font: {
              family: "Commons Pro",
              size: 13,
            },
          },
          stacked: selectedMetric.timeSeriesDisplayMode === "stack",
        },
      },
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedMetric, datasets, dateLabels]);

  return <Bar data={{ labels: dateLabels, datasets }} options={options} />;
};

const createDiagonalPattern = (color = "black") => {
  const shape = document.createElement("canvas");
  shape.width = 10;
  shape.height = 10;
  const c = shape.getContext("2d")!;
  c.strokeStyle = color;
  c.beginPath();
  c.moveTo(2, 0);
  c.lineTo(10, 8);
  c.stroke();
  c.beginPath();
  c.moveTo(0, 8);
  c.lineTo(2, 10);
  c.stroke();
  return c.createPattern(shape, "repeat");
};
